<template>
  <div>
    <app-table v-bind="$attrs" class="w-full">
      <template v-slot:table-body>
        <tr
          v-for="budget in budgets"
          :key="'budgets-table-' + budget.id"
          class="border border-collapse border-dashed border-yellow border-t-0 border-l-0 border-r-0 h-12 hover:bg-yellow-lightest"
        >
          <td width="150px">
            <span class="text-lg ml-3">
              {{ $filters.dateFormat(budget.paymentDate) }}
            </span>
          </td>
          <td width="120px">
            <span class="text-lg">
              {{ getContentName(budget) }}
            </span>
          </td>
          <td width="460px">
            <span class="text-lg">
              {{ budget.remarks }}
            </span>
          </td>
          <td width="120px" style="text-align: right">
            <span class="text-lg">{{
              budget.amount ? budget.amount.toLocaleString() + ' 円' : ''
            }}</span>
          </td>
          <td width="20px">
            <approved-icn class="20px mx-3" height="20px" />
          </td>
          <td width="88px">
            <button
              class="border-4 border-yellow rounded-full ma-1 h-10 px-2"
              @click="openBudgetPreviewRegistered(budget.id)"
            >
              <div class="flex justify-center items-center">
                <edit-icn width="20px" height="20px" />
                <span class="text-yellow font-bold text-lg">&nbsp;編集</span>
              </div>
            </button>
          </td>
          <td class="text-center" width="20px">
            <Menu as="div" class="relative flex text-left">
              <MenuButton>
                <actions-icn width="20px" height="20px" class="yellow-icon" />
              </MenuButton>
              <transition
                enter-active-class="transition duration-100 ease-out"
                enter-from-class="transform scale-95 opacity-0"
                enter-to-class="transform scale-100 opacity-100"
                leave-active-class="transition duration-75 ease-in"
                leave-from-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0"
              >
                <MenuItems
                  class="absolute right-8 mt-0 origin-top-right bg-white divide-y divide-gray-100 shadow-lg focus:outline-none"
                  style="border-radius: 20px; width: 168px; padding: 2px 2px; top:-10px;"
                >
                  <div
                    class="border-4 border-yellow"
                    style="border-radius: 20px;"
                  >
                    <menu-item v-slot="{ active }">
                      <button
                        :class="[
                          active
                            ? 'bg-yellow-lightest text-yellow'
                            : 'text-gray-900',
                          'group flex rounded-md items-center w-full px-2 py-2 text-sm',
                        ]"
                        @click="showDeletionConfirmation(budget)"
                      >
                        <div class="flex flex-row items-center">
                          <TrashIcn
                            fill-rule="evenodd"
                            class="mr-1 yellow-icon"
                            width="20px"
                            height="20px"
                          />
                          <span class="text-yellow text-lg">削除</span>
                        </div>
                      </button>
                    </menu-item>
                  </div>
                </MenuItems>
              </transition>
            </Menu>
          </td>
        </tr>
      </template>
    </app-table>
    <AppConfirm color="yellow" />
  </div>
</template>

<script>
import { ref, watch } from 'vue'
import AppTable from '@/components/ui/AppTable'
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue'
import ActionsIcn from '@/assets/icons/actions.svg?inline'
import AppConfirm from '@/components/ui/AppConfirm'
import EditIcn from '@/assets/icons/edit-yellow-circle.svg?inline'
import ApprovedIcn from '@/assets/icons/approved.svg?inline'
import TrashIcn from '@/assets/icons/trash.svg?inline'
import { useBudgets } from '@/services/useBudgets'
import { useBudgetDialog } from '@/services/useBudgetDialog'
import { useBudgetForm } from '@/services/useBudgetForm'
import { useConfirmation } from '@/services/useConfirmation'

export default {
  name: 'BudgetsTable',

  components: {
    AppTable,
    Menu,
    MenuButton,
    MenuItems,
    MenuItem,
    ActionsIcn,
    EditIcn,
    ApprovedIcn,
    TrashIcn,
    AppConfirm,
  },

  setup() {
    const { budgets: budgets } = useBudgets()
    const { items } = useBudgetForm()
    const { openBudgetPreviewRegistered } = useBudgetDialog()
    const currentBudget = ref(null)
    const {
      toggleShowConfirmation,
      isConfirmed,
      showConfirmation,
    } = useConfirmation()

    /**
     * 予算削除確認画面を表示する
     *
     * @param {object} budget 予算テーブルの行データ
     */
    const showDeletionConfirmation = budget => {
      currentBudget.value = budget
      toggleShowConfirmation(true)
    }

    const getContentName = budget => {
      return items.find(content => content.id === budget.contentId).name
    }

    const deleteItem = async () => {
      // execute
      const { deleteBudget } = useBudgets()
      const budgetId = currentBudget?.value?.id ?? null
      if (budgetId !== null) {
        await deleteBudget(budgetId)
      }

      // tear down
      currentBudget.value = null
      toggleShowConfirmation(false)
    }

    watch(isConfirmed, isConfirmed => {
      if (isConfirmed) {
        deleteItem()
      }
    })
    watch(showConfirmation, (showConfirmation, prevShowConfirmation) => {
      const isClosed = prevShowConfirmation && !showConfirmation
      if (isClosed) {
        currentBudget.value = null
      }
    })
    return {
      budgets,
      getContentName,
      openBudgetPreviewRegistered,
      showDeletionConfirmation,
    }
  },
}
</script>
<style lang="scss"></style>
